import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

interface ServiceWorkerRegistrationLike {
    unregister(): Promise<boolean>
}

type TimerHandle = ReturnType<typeof setTimeout>
type ScheduleTimer = (callback: () => void, timeoutMs?: number) => TimerHandle
type CancelTimer = (handle: TimerHandle) => void

interface LazyRecoveryEnvironment {
    caches?: Pick<CacheStorage, 'keys' | 'delete'>
    location?: Pick<Location, 'reload'>
    serviceWorker?: {
        getRegistrations?(): Promise<readonly ServiceWorkerRegistrationLike[]>
    }
    storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
    setTimeout: ScheduleTimer
    clearTimeout: CancelTimer
}

interface LoadLazyModuleOptions {
    moduleName: string
    timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 8000

function getDefaultEnvironment(): LazyRecoveryEnvironment {
    const timerHost = typeof window !== 'undefined' ? window : globalThis
    return {
        caches: typeof caches !== 'undefined' ? caches : undefined,
        location: typeof window !== 'undefined' ? window.location : undefined,
        serviceWorker: typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
        storage: typeof sessionStorage !== 'undefined' ? sessionStorage : undefined,
        setTimeout: (...args) => timerHost.setTimeout(...args),
        clearTimeout: (...args) => timerHost.clearTimeout(...args),
    }
}

function getRecoveryStorageKey(moduleName: string): string {
    return `sb:lazy-import-recovery:${moduleName}`
}

function createTimeoutError(moduleName: string, timeoutMs: number): Error {
    return new Error(`Timed out loading lazy module "${moduleName}" after ${timeoutMs}ms`)
}

function isRecoverableLazyImportError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error)
    return /timed out loading lazy module/i.test(message)
        || /failed to fetch dynamically imported module/i.test(message)
        || /loading chunk/i.test(message)
        || /chunkloaderror/i.test(message)
}

async function clearClientCaches(env: LazyRecoveryEnvironment): Promise<void> {
    try {
        const registrations = await env.serviceWorker?.getRegistrations?.()
        await Promise.all((registrations ?? []).map(registration => registration.unregister()))
    } catch {
        // best effort
    }

    try {
        const cacheNames = await env.caches?.keys?.()
        await Promise.all((cacheNames ?? []).map(name => env.caches?.delete(name)))
    } catch {
        // best effort
    }
}

async function recoverLazyImportOnce(moduleName: string, error: unknown, env: LazyRecoveryEnvironment): Promise<void> {
    if (!isRecoverableLazyImportError(error)) {
        return
    }

    const storage = env.storage
    const recoveryKey = getRecoveryStorageKey(moduleName)
    if (storage?.getItem(recoveryKey) === '1') {
        storage.removeItem(recoveryKey)
        return
    }

    storage?.setItem(recoveryKey, '1')
    console.warn(`[lazy-import] recovering ${moduleName} after failed lazy load`, error)
    await clearClientCaches(env)
    env.location?.reload()
}

export async function loadLazyModuleWithRecovery<T>(
    loader: () => Promise<{ default: T }>,
    options: LoadLazyModuleOptions,
    env: LazyRecoveryEnvironment = getDefaultEnvironment(),
): Promise<{ default: T }> {
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
    let timeoutId: TimerHandle | null = null

    try {
        return await Promise.race([
            loader(),
            new Promise<never>((_, reject) => {
                timeoutId = env.setTimeout(() => reject(createTimeoutError(options.moduleName, timeoutMs)), timeoutMs)
            }),
        ])
    } catch (error) {
        await recoverLazyImportOnce(options.moduleName, error, env)
        throw error
    } finally {
        if (timeoutId != null) {
            env.clearTimeout(timeoutId)
        }
    }
}

export function lazyWithRecovery<T extends ComponentType<any>>(
    loader: () => Promise<{ default: T }>,
    options: LoadLazyModuleOptions,
): LazyExoticComponent<T> {
    return lazy(() => loadLazyModuleWithRecovery(loader, options))
}

export const __test__ = {
    createTimeoutError,
    getRecoveryStorageKey,
    isRecoverableLazyImportError,
}
